import { useEffect, useCallback, useState } from "react";
import useIsMountedRef from "../../hooks/useIsMountedRef";
import { useTranslation } from "react-i18next";
import YassirLoader from "../../components/YassirLoader";
import { useDispatch, useSelector } from "react-redux";
import { loadCountries } from "../../actions/countriesActions";
import { setCountry } from "../../actions/setCountriesActions";
import { loadProfile } from "../../actions/profileActions";
import { setIsConnected } from "../../actions/userActions";
import { setIntl } from "../../actions/intlAction";
import { COUNTRY_MAP } from "../../utils/constants";
import { isSessionExpired } from "../../utils/apiClient";
import { Alert, Button, Stack } from "@mui/material";

export default function Auth({ children }) {
  const { countries, activeCountry } = useSelector((state) => state.countries);
  const { profile, status, error } = useSelector((state) => state.profile);
  const { isConnected } = useSelector((state) => state.account);

  const { i18n, t } = useTranslation();

  const [loaded, setLoaded] = useState(false);
  const isMountedRef = useIsMountedRef();
  const dispatch = useDispatch();

  const fetchData = useCallback(async () => {
    if (isMountedRef.current) {
      try { localStorage.removeItem("token"); } catch (_) {}
      const profileResponse = await dispatch(loadProfile());
      dispatch(setIsConnected(Boolean(profileResponse)));
      await dispatch(loadCountries());
      setLoaded(true);
    }
  }, [isMountedRef, dispatch]);

  useEffect(() => {
    const endSession = () => {
      dispatch(setIsConnected(false));
      if (window.location.pathname !== '/') window.location.replace('/');
    };
    const onStorage = (event) => { if (event.key === 'fleet-logout') endSession(); };
    window.addEventListener('fleet-session-expired', endSession);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('fleet-session-expired', endSession);
      window.removeEventListener('storage', onStorage);
    };
  }, [dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (status === 200 && profile?.config?.INFOS)
      dispatch(setCountry(profile?.config?.INFOS));
    else if (countries) {
      const active = countries.find(
        (country) => country.code === COUNTRY_MAP[activeCountry]
      );
      dispatch(setCountry(active ?? countries[0]));
    }
  }, [dispatch, profile, status, countries, activeCountry]);

  const resolveStatus = useCallback(() => {
    if (!isSessionExpired(error?.error)) return;

    dispatch(setIsConnected(false));

    if (window.location.pathname !== "/") {
      window.location.replace("/");
    }
  }, [dispatch, error]);

  useEffect(() => {
    i18n.changeLanguage(
      localStorage.getItem("fleetLangage") ||
        (["fr", "ar"].includes(
          navigator.language.split("-")[0].toLowerCase()
        ) &&
          navigator.language.split("-")[0].toLowerCase()) ||
        window.localStorage.i18nextLng
    );
    dispatch(
      setIntl(
        localStorage.getItem("fleetLangage") ||
          (["fr", "ar"].includes(
            navigator.language.split("-")[0].toLowerCase()
          ) &&
            navigator.language.split("-")[0].toLowerCase()) ||
          window.localStorage.i18nextLng
      )
    );
  }, [i18n, dispatch]);

  useEffect(() => {
    resolveStatus();
  }, [resolveStatus]);

  if (loaded && error?.error && !isSessionExpired(error.error)) {
    return <Stack spacing={2} sx={{ p: 3 }}>
      <Alert severity="error">{t('auth.serviceUnavailable')}</Alert>
      <Button onClick={() => window.location.reload()}>{t('auth.retry')}</Button>
    </Stack>;
  }

  if (!loaded || !countries) {
    return <YassirLoader />;
  }

  if (isConnected && !profile) {
    return <YassirLoader />;
  }

  return children;
}
